import { store } from './session';
import { withTimeout } from './timeout';
import { labelFit, labelSizeMm, labelSvg, loadZplLabels } from './zpl/render';

// Printing labels through the computer's own printer driver: the label is drawn at its true size
// and sent with the browser's print. Chrome / Edge started with --kiosk-printing print at once
// to the default printer, without a dialog (docs/yazici-kurulumu.md).

export interface PrintPage {
  /** SVG or HTML of one page, drawn to fill it */
  readonly markup: string;
  readonly widthMm: number;
  readonly heightMm: number;
  /** printed turned by 90°: the page goes out as heightMm × widthMm */
  readonly rotate?: boolean;
}

const ROTATE_KEY = 'wms.print.rotated';
// a marketplace's 4×6 inch label is 101.6 × 152.4 mm: a size within 3 mm is the same roll
const sameRoll = (size: string, widthMm: number, heightMm: number) => {
  const [w, h] = size.split('x').map(Number);
  return Math.abs((w ?? 0) - widthMm) <= 3 && Math.abs((h ?? 0) - heightMm) <= 3;
};

/**
 * Label sizes this computer prints turned by 90° ("yatay bas"): some drivers set the roll up the
 * other way round and print the label sideways or shrunk. Kept per computer and per roll size.
 */
function rotatedSizes(): Set<string> {
  try {
    const saved: unknown = JSON.parse(store.get(ROTATE_KEY) ?? '[]');
    return new Set(Array.isArray(saved) ? saved.filter((s): s is string => typeof s === 'string') : []);
  } catch {
    return new Set();
  }
}

export function isRotated(size: string): boolean {
  return rotatedSizes().has(size.replace(/\s/g, ''));
}

export function setRotated(size: string, on: boolean): void {
  const sizes = rotatedSizes();
  if (on) sizes.add(size.replace(/\s/g, ''));
  else sizes.delete(size.replace(/\s/g, ''));
  store.set(ROTATE_KEY, sizes.size > 0 ? JSON.stringify([...sizes]) : null);
}

/** Prints pages in a hidden frame; resolves once the print was handed to the browser. */
export async function printPages(pages: readonly PrintPage[], title = 'Etiket'): Promise<void> {
  if (pages.length === 0) return;
  // one named page per size, so a 100×150 label and a 50×30 one keep their own paper size;
  // a turned label goes out on a page of the other orientation, drawn rotated into it
  const paper = (p: PrintPage) => (p.rotate ? `${p.heightMm}x${p.widthMm}` : `${p.widthMm}x${p.heightMm}`);
  const sizes = [...new Set(pages.map(paper))];
  const css = sizes
    .map((s, i) => {
      const [w, h] = s.split('x');
      return `@page s${i} { size: ${w}mm ${h}mm; margin: 0; } .s${i} { page: s${i}; width: ${w}mm; height: ${h}mm; }`;
    })
    .join('\n');
  const body = pages
    .map((p) => {
      const markup = p.rotate
        ? `<div class="turn" style="width:${p.widthMm}mm;height:${p.heightMm}mm;transform:translate(${p.heightMm}mm,0) rotate(90deg)">${p.markup}</div>`
        : p.markup;
      return `<div class="page s${sizes.indexOf(paper(p))}">${markup}</div>`;
    })
    .join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${title}</title><style>
@page { margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
.page { overflow: hidden; break-after: page; page-break-after: always; }
.page:last-child { break-after: auto; page-break-after: auto; }
.page > svg, .page > img, .turn > svg, .turn > img { display: block; width: 100%; height: 100%; }
.turn { transform-origin: 0 0; }
${css}
</style></head><body>${body}</body></html>`;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;';
  const loaded = new Promise<void>((resolve) => frame.addEventListener('load', () => resolve(), { once: true }));
  frame.srcdoc = html;
  document.body.appendChild(frame);
  await loaded;
  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  if (!doc || !win) throw new Error('Yazdırma penceresi açılamadı');
  await doc.fonts?.ready;
  await Promise.all([...doc.images].map((img) => img.decode().catch(() => undefined)));
  const cleanup = () => setTimeout(() => frame.remove(), 1_000);
  win.addEventListener('afterprint', cleanup, { once: true });
  setTimeout(() => frame.isConnected && frame.remove(), 120_000);
  win.focus();
  win.print();
}

/**
 * A ZPL document (marketplace or our own label) printed through the driver; returns its warnings.
 * Each label goes out on a page of its own size with no margins, at true size. Nothing past its
 * edge is cut off: a text is narrowed on its line, and a label whose codes would run past the edge
 * is shrunk onto the page (labelFit), which the warnings say.
 */
export async function printZpl(zpl: string, title?: string): Promise<{ readonly pages: number; readonly warnings: readonly string[] }> {
  const labels = await loadZplLabels(zpl);
  if (labels.length === 0) throw new Error('Etikette basılacak bir şey yok (ZPL okunamadı)');
  const pages: PrintPage[] = [];
  const warnings = labels.flatMap((l) => l.warnings);
  const turned = rotatedSizes();
  for (const label of labels) {
    const { width, height } = labelSizeMm(label);
    const svg = labelSvg(label);
    const { scale } = labelFit(label);
    if (scale < 1) warnings.push(`etiket sayfaya sığmadığı için %${Math.floor(scale * 100)} boyutunda basıldı`);
    const rotate = [...turned].some((size) => sameRoll(size, width, height));
    for (let copy = 0; copy < label.copies; copy++) pages.push({ markup: svg, widthMm: width, heightMm: height, rotate });
  }
  await printPages(pages, title);
  return { pages: pages.length, warnings: [...new Set(warnings)] };
}

/**
 * A PDF in a frame that has not loaded by then never will: a browser without a PDF viewer (Chrome on
 * Android) downloads it instead and the frame fires no load.
 */
const PDF_LOAD_MS = 15_000;

/** A PDF document (e-İrsaliye, a marketplace label sent as PDF) through the browser's PDF viewer. */
export async function printPdf(blob: Blob): Promise<void> {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;';
  const gone = () => {
    frame.remove();
    URL.revokeObjectURL(url);
  };
  const loaded = new Promise<void>((resolve) => frame.addEventListener('load', () => resolve(), { once: true }));
  frame.src = url;
  document.body.appendChild(frame);
  try {
    await withTimeout(loaded, PDF_LOAD_MS, 'Bu cihaz PDF basamıyor (PDF açılmadı)');
  } catch (e) {
    gone();
    throw e;
  }
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  setTimeout(gone, 120_000);
}
